import axios from "axios";
import { useState, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import { Close, Search, UploadFile } from "@mui/icons-material";
import LoadingPanel from "../../../components/common/LoadingPanel";
import CreatedByText from "../../../components/common/CreatedByText";
import ExpandableView from "../../../components/common/ExpandableView";
import CheckboxComponent from "../../../components/common/CheckboxComponent";
import { getEvidenceTypeExtension } from "../../../common/utils";
import ApplicationLogoVertical from "../../../components/common/ApplicationLogoVertical";
import { downloadBlob } from "../../../common/EntitiesManagement";

export default function AddOrEditContractor() {
  const {
    contractorModel,
    setContractorModel,
    locations,
    setCity,
    cities,
    config,
    CONTRACTOR_API,
  } = useGlobalContext();

  const [isLoadingLogo, setIsLoadingLogo] = useState(false);
  const [logoBlob, setLogoBlob] = useState(undefined);
  const inputFile = useRef(null);

  useEffect(() => {
    if (contractorModel?.companyLogo) {
      getLogoBlob(contractorModel.Id);
    }
  }, []);

  const getLogoBlob = (contractorId) => {
    if (contractorId > 0) {
      downloadBlob(
        `${CONTRACTOR_API}/${contractorId}/logo/download`,
        config,
        setLogoBlob,
        setIsLoadingLogo,
      );
    }
  };

  const handleChange = (e) => {
    setContractorModel({
      ...contractorModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleIdentificationTypeChange = (identificationType) => {
    if (identificationType) {
      setContractorModel({
        ...contractorModel,
        IdentificationType: identificationType,
      });
    }
  };

  const handleStateChange = (currentState) => {
    if (!currentState) {
      setContractorModel({
        ...contractorModel,
        StateId: 0,
        State: null,
        CityId: 0,
        City: null,
      });
      return;
    }

    setContractorModel({
      ...contractorModel,
      StateId: currentState.Id,
      State: currentState,
      CityId: 0,
      City: null,
    });
  };

  const handleCityChange = (currentCity) => {
    if (!currentCity) {
      setContractorModel({
        ...contractorModel,
        CityId: 0,
        City: null,
      });
      return;
    }

    setContractorModel({
      ...contractorModel,
      CityId: currentCity.Id,
      City: currentCity,
    });
  };

  const [states, setStates] = useState(null);
  const [citiesData, setCities] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState({ show: false });

  const validateStateChange = () => {
    if (contractorModel?.StateId > 0) {
      const selectedState = states?.find(
        (element) => element.Id === contractorModel?.StateId,
      );
      if (selectedState) {
        setCity(selectedState.Code);
      }
    }
  };

  useEffect(() => {
    validateStateChange();
  }, [contractorModel?.StateId, states]);

  useEffect(() => {
    if (locations.Ok && locations.Data.length > 0) {
      setStates(locations.Data);
    }
  }, [locations]);

  useEffect(() => {
    if (cities?.Ok && cities.Data.length > 0) {
      setCities(cities.Data);
    }
  }, [cities]);

  const isSearchAvailable = () =>
    contractorModel?.Identification?.replaceAll(" ", "").length > 0;

  const getContractorInfoByIdentification = async () => {
    try {
      if (contractorModel?.Identification?.replaceAll(" ", "").length === 0) {
        return;
      }
      setShowWarning({ show: false });
      setIsLoading(true);
      const filteringConfig = {
        ...config,
        headers: {
          ...config.headers,
          Filter: JSON.stringify([
            {
              Name: "Identification",
              FriendlyName: localize.identification,
              FilterValue: { Value: contractorModel.Identification },
              Value: contractorModel.Identification,
            },
            {
              Name: "IdentificationType",
              FriendlyName: localize.identification_type,
              FilterValue: { Value: contractorModel.IdentificationType.Id },
              Value: contractorModel.IdentificationType.Id,
            },
          ]),
          pagination: 0,
        },
      };
      const data = await axios.get(CONTRACTOR_API, filteringConfig);

      if (data?.data?.Data && data?.data?.Data.length > 0) {
        setContractorModel({
          ...data?.data?.Data[0],
          IdentificationType: {
            Id: data?.data?.Data[0].IdentificationType,
            Name: data?.data?.Data[0].IdentificationType,
          },
        });

        setShowWarning({
          show: true,
          severity: "warning",
          message: localize.found_contractor_warning,
        });
        return;
      }

      setShowWarning({
        show: true,
        severity: "info",
        message: localize.no_data,
      });
    } catch (error) {
      setShowWarning({
        show: true,
        severity: "error",
        message: `${localize.error_unexpected}: ${error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Enter") {
      getContractorInfoByIdentification();
    }
  };

  const onReportModelEnabledOnChange = (e) => {
    if (!e) {
      setContractorModel({
        ...contractorModel,
        reportSetup: {
          enabled: false,
        },
      });
      return;
    }

    setContractorModel({
      ...contractorModel,
      reportSetup: {
        enabled: e.target.checked,
        data: {
          logo: false,
          name: false,
          identification: false,
          address: false,
          phoneNumber: false,
          email: false,
        },
      },
    });
  };

  const getShowOnReportHeader = () => (
    <Box className="flex w-full mt-2">
      <FormGroup>
        <FormControlLabel
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
          control={
            <Switch
              checked={contractorModel?.reportSetup?.enabled}
              onChange={onReportModelEnabledOnChange}
              slotProps={{ input: { "aria-label": "controlled" } }}
            />
          }
          label={localize.show_report_header}
        />
      </FormGroup>
    </Box>
  );

  const onShowDataCheckedChanged = (e) => {
    setContractorModel({
      ...contractorModel,
      reportSetup: {
        ...contractorModel.reportSetup,
        data: {
          ...contractorModel.reportSetup?.data,
          [e.target.name]: e.target.checked,
        },
      },
    });
  };

  const onSelectButtonClick = () => {
    // `current` points to the mounted file input element
    inputFile.current.click();
  };

  const changeFilePath = (e) => {
    if (e.target.files) {
      setContractorModel({
        ...contractorModel,
        File: e.target.files[0],
        companyLogo: e.target.files[0].name,
      });

      setLogoBlob(URL.createObjectURL(e.target.files[0]));
      return;
    }

    setContractorModel({
      ...contractorModel,
      File: undefined,
      companyLogo: undefined,
    });
  };

  const getLogoComponent = () => {
    return (
      <Box className="flex flex-col">
        <Box className="flex gap-2 w-full items-center">
          <Tooltip title={localize.upload_logo}>
            <IconButton
              onClick={() => {
                onSelectButtonClick();
              }}
            >
              <UploadFile
                className="info_icon_color"
                sx={{ width: "16px", height: "16px" }}
              />
            </IconButton>
          </Tooltip>

          {isLoadingLogo ? (
            <CircularProgress size={20} />
          ) : (
            <ApplicationLogoVertical
              height={80}
              width={100}
              path={
                !contractorModel?.companyLogo ||
                contractorModel?.companyLogo.replace(" ", "") === ""
                  ? `${process.env.PUBLIC_URL}/img/app_report_header.png`
                  : logoBlob ??
                    `${process.env.PUBLIC_URL}/img/app_report_header.png`
              }
            />
          )}

          <input
            type="file"
            id="file"
            accept={getEvidenceTypeExtension(1)}
            ref={inputFile}
            onChange={changeFilePath}
            style={{ display: "none" }}
          />
        </Box>

        {contractorModel?.companyLogo && (
          <Box className="flex gap-2 w-full items-center">
            <Tooltip title={localize.delete_logo}>
              <IconButton
                onClick={() => {
                  setContractorModel({
                    ...contractorModel,
                    File: undefined,
                    companyLogo: undefined,
                  });

                  setLogoBlob(undefined);
                }}
              >
                <Close sx={{ color: "red", width: "18px", height: "18px" }} />
              </IconButton>
            </Tooltip>

            <label className="report_little_text report_disabled_text">
              {contractorModel?.companyLogo?.replace(/^[^_]+_/, "")}
            </label>
          </Box>
        )}
      </Box>
    );
  };

  const getReportHeaderComponents = () => {
    if (contractorModel?.reportSetup && contractorModel.reportSetup.enabled) {
      return (
        <Box className="flex flex-col w-full">
          {getShowOnReportHeader()}
          <ExpandableView
            components={[
              {
                Name: localize.report_header,
                Component: (
                  <Box className="flex flex-col">
                    <Box className="flex gap-2">
                      <Box sx={{ width: "50%" }}>
                        <CheckboxComponent
                          propertyName="logo"
                          value={contractorModel?.reportSetup?.data?.logo}
                          text={localize.show_logo}
                          onValueChanged={onShowDataCheckedChanged}
                        />
                      </Box>

                      {contractorModel?.reportSetup?.data?.logo && (
                        <Box sx={{ width: "50%" }}>{getLogoComponent()}</Box>
                      )}
                    </Box>
                    <hr />
                    <Box className="flex gap-2">
                      <Box sx={{ width: "50%" }}>
                        <CheckboxComponent
                          propertyName="name"
                          value={contractorModel?.reportSetup?.data?.name}
                          text={localize.show_name}
                          onValueChanged={onShowDataCheckedChanged}
                        />
                      </Box>
                      <Box sx={{ width: "50%", paddingLeft: "10px" }}>
                        <CheckboxComponent
                          propertyName="identification"
                          value={
                            contractorModel?.reportSetup?.data?.identification
                          }
                          text={localize.show_identification}
                          onValueChanged={onShowDataCheckedChanged}
                        />
                      </Box>
                    </Box>
                    <Box className="w-full flex gap-2">
                      <Box sx={{ width: "50%" }}>
                        <CheckboxComponent
                          propertyName="address"
                          value={contractorModel?.reportSetup?.data?.address}
                          text={localize.show_address}
                          onValueChanged={onShowDataCheckedChanged}
                        />
                      </Box>
                      <Box sx={{ width: "50%", paddingLeft: "10px" }}>
                        <CheckboxComponent
                          propertyName="phoneNumber"
                          value={
                            contractorModel?.reportSetup?.data?.phoneNumber
                          }
                          text={localize.show_phone_number}
                          onValueChanged={onShowDataCheckedChanged}
                        />
                      </Box>
                    </Box>
                    <Box className="w-full flex gap-2">
                      <Box sx={{ width: "50%" }}>
                        <CheckboxComponent
                          propertyName="email"
                          value={contractorModel?.reportSetup?.data?.email}
                          text={localize.show_email}
                          onValueChanged={onShowDataCheckedChanged}
                        />
                      </Box>
                    </Box>
                  </Box>
                ),
              },
            ]}
          />
        </Box>
      );
    } else {
      return getShowOnReportHeader();
    }
  };

  return (
    <div>
      <Container
        sx={{ paddingLeft: "0px !important", paddingRight: "0px !important" }}
        style={{ width: "100%" }}
      >
        <div className="w-full">
          <section className="flex justify-center content-center items-center ">
            {isLoading ? (
              <LoadingPanel />
            ) : (
              <section id="ReportData" className="w-full">
                <div className="flex justify-between my-4">
                  <Combobox
                    isRequired
                    id="IdentificationType"
                    handleValueChange={handleIdentificationTypeChange}
                    fieldValue={contractorModel?.IdentificationType ?? null}
                    fieldOptions={[
                      {
                        Id: "Cédula de ciudadanía",
                        Name: "Cédula de ciudadanía",
                      },
                      { Id: "Nit", Name: "Nit" },
                      { Id: "Pasaporte", Name: "Pasaporte" },
                      {
                        Id: "Cédula de extranjería",
                        Name: "Cédula de extranjería",
                      },
                      { Id: "Otro", Name: "Otro" },
                    ]}
                    fieldLabel={localize.identification_type}
                  />
                  <div className="mx-2" />
                  <Box className="flex gap-2 items-center w-full">
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      size="small"
                      fullWidth
                      name="Identification"
                      value={contractorModel.Identification}
                      id="Identification"
                      label={localize.identification}
                      variant="outlined"
                      onChange={handleChange}
                      onKeyUp={onKeyUp}
                      autoComplete="off"
                    />

                    <Tooltip title={localize.contractor_search_tooltip}>
                      <IconButton
                        disabled={!isSearchAvailable()}
                        style={{
                          color: isSearchAvailable() ? "#10366e" : "lightgray",
                        }}
                        onClick={getContractorInfoByIdentification}
                      >
                        <Search
                          sx={{
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </div>

                {showWarning?.show && (
                  <Alert className="mb-3" severity={showWarning.severity}>
                    <label className="text-xs">{showWarning.message}</label>
                  </Alert>
                )}

                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Name"
                  id="Name"
                  value={contractorModel.Name}
                  label={localize.name}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
                {/* States */}
                <div className="flex justify-between my-4">
                  <Combobox
                    isRequired
                    id="State"
                    handleValueChange={handleStateChange}
                    fieldValue={contractorModel?.State}
                    fieldOptions={states ?? []}
                    fieldLabel={localize.department}
                  />

                  {/* Cities */}
                  <div className="mx-2" />
                  <Combobox
                    isRequired
                    id="City"
                    handleValueChange={handleCityChange}
                    fieldValue={contractorModel?.City}
                    fieldOptions={
                      contractorModel?.State ? citiesData ?? [] : []
                    }
                    fieldLabel={localize.city}
                  />
                </div>
                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Address"
                  id="Address"
                  value={contractorModel.Address}
                  label={localize.address}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />

                <div className="flex justify-between my-4">
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="PhoneNumber"
                    id="PhoneNumber"
                    value={contractorModel.PhoneNumber}
                    label={localize.phone_number}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  <div className="mx-2" />
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="Email"
                    id="Email"
                    value={contractorModel.Email}
                    label={localize.email}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  value={contractorModel.ContactName}
                  name="ContactName"
                  id="ContactName"
                  label={localize.contact_name}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
              </section>
            )}
          </section>
        </div>

        {contractorModel?.Id > 0 && getReportHeaderComponents()}

        <CreatedByText entity={contractorModel} />
      </Container>
    </div>
  );
}
